// src/pages/Remediation.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { usePageSize } from '../hooks/usePageSize';
import { useRBAC } from '../hooks/useRBAC';
import { useHasLicense } from '../contexts/LicenseContext';
import { useRemediationWorkflows } from '../contexts/FeatureFlagsContext';
import Navigation from '../components/Navigation';
import { PINNED_PANEL_TOP } from '../components/PinnedNavigation';
import Button from '../components/Buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import { Search, Edit, CheckCircle, ExternalLink, Shield, Link2, User, ChevronDown, ChevronRight, Filter, Eye, Workflow, X, Loader } from 'lucide-react';
import RiskManagementModal from '../components/RiskManagementModal';
import ActivityLogTab from '../components/ActivityLogTab';
import RiskAcceptanceModal from '../components/RiskAcceptanceModal';
import RiskApprovalModal from '../components/RiskApprovalModal';
import AssignRiskModal from '../components/AssignRiskModal';
import BulkAssignRiskModal from '../components/BulkAssignRiskModal';
import CreateJiraIssueModal from '../components/CreateJiraIssueModal';
import CreateServiceNowIncidentModal from '../components/CreateServiceNowIncidentModal';
import { useModuleAvailability } from '../hooks/useModuleAvailability';
import WorkflowStepPanel from '../components/WorkflowStepPanel';
import WorkflowTimeline from '../components/WorkflowTimeline';
import { parseRemediationSteps } from '../utils/remediationSteps';

/**
 * Resolves a `?remediationId=<id>` deep link (the target of the "workflow step assigned" email)
 * against the filtered list: the row's id and the 1-based page it sits on for `pageSize`, or
 * null when the id is not a number or not in the list.
 */
export const findDeepLinkTarget = (rows, remediationIdParam, pageSize) => {
    const id = parseInt(remediationIdParam, 10);
    if (!Number.isFinite(id) || !pageSize) return null;
    const index = (rows || []).findIndex(r => r.remediationID === id);
    if (index < 0) return null;
    return { remediationID: rows[index].remediationID, page: Math.floor(index / pageSize) + 1 };
};

/**
 * Expanded Workflow section of one remediation card. Both children mount immediately (their
 * requests run in parallel) but stay hidden behind a centered spinner until each has reported its
 * first load through `onLoaded` (any outcome, errors included), so the section appears in one
 * piece instead of pushing the card down twice. The header row with the close control renders at
 * once so the section can be closed while loading; `onReady(remediationID)` fires when it is
 * visible so the deep-link scroll lands on the final layout.
 */
const WorkflowSection = ({ remediation, refreshKey, onChanged, onClose, onReady }) => {
    const [panelLoaded, setPanelLoaded] = useState(false);
    const [timelineLoaded, setTimelineLoaded] = useState(false);
    const ready = panelLoaded && timelineLoaded;
    const onReadyRef = useRef(onReady);
    useEffect(() => { onReadyRef.current = onReady; });
    useEffect(() => {
        if (ready && onReadyRef.current) onReadyRef.current(remediation.remediationID);
    }, [ready, remediation.remediationID]);

    return (
        <div
            id={`workflow-section-${remediation.remediationID}`}
            className="mt-4 pt-4 border-t space-y-4"
            data-testid={`workflow-section-${remediation.remediationID}`}
        >
            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-content-primary">Workflow</span>
                <button
                    type="button"
                    aria-label="Close workflow section"
                    title="Close"
                    className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    onClick={onClose}
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
            {!ready && (
                <div className="flex items-center justify-center py-6" data-testid="workflow-section-loading">
                    <Loader className="h-5 w-5 animate-spin text-indigo-600" aria-label="Loading workflow" />
                </div>
            )}
            <div hidden={!ready} className="space-y-4" data-testid="workflow-section-body">
                <WorkflowStepPanel
                    riskId={remediation.riskId}
                    remediationId={remediation.remediationID}
                    remediationTitle={remediation.remediationTitle}
                    remediationPlan={remediation.actionDescription}
                    dealId={remediation.dealID || null}
                    businessUnitId={remediation.businessUnitId || null}
                    onChanged={onChanged}
                    onLoaded={() => setPanelLoaded(true)}
                />
                <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Workflow history</h4>
                    <WorkflowTimeline
                        riskId={remediation.riskId}
                        refreshKey={refreshKey}
                        onLoaded={() => setTimelineLoaded(true)}
                    />
                </div>
            </div>
        </div>
    );
};

const Remediation = () => {
    const { user } = useAuth();
    const apiCall = useApi();
    const navigate = useNavigate();
    const { canAssess, canManageOrganizationRisks, canManageDeal, canManageBusinessUnit, canUpdateRiskViaAssignment } = useRBAC();
    const [searchParams] = useSearchParams();
    const hasLicense = useHasLicense();
    const canManageOrgRisks = canManageOrganizationRisks();
    // !! coercion is load-bearing: dealID/businessUnitId are 0 for org-level rows, and `0 && x`
    // returns the number 0 — which React renders as a literal "0" in `{cond && <X/>}`.
    const canManageRisk = (remediation) => !!(canManageOrgRisks
        || (remediation.businessUnitId && canManageBusinessUnit(remediation.businessUnitId))
        || (remediation.dealID && canManageDeal(remediation.dealID)));
    const canUpdateRisk = (remediation) => !!(canManageRisk(remediation)
        || canUpdateRiskViaAssignment(remediation.remediationAssignTo ? parseInt(remediation.remediationAssignTo) : null));

    // All possible filter types
    const allFilterTypes = ['Deals', 'BusinessUnits', 'Enterprise', 'TPRM', 'Ransomware', 'DarkWeb', 'AI', 'GRC', 'External'];

    // Filter to only licensed types
    const licensedFilterTypes = useMemo(() => {
        return allFilterTypes.filter(type => type === 'External' || hasLicense(type));
    }, [hasLicense]);

    // Initialize filterTypes based on URL parameter (e.g., ?types=Deals,Ransomware)
    const getInitialFilterTypes = () => {
        const typesParam = searchParams.get('types');
        if (typesParam) {
            const selectedTypes = typesParam.split(',');
            return {
                Deals: selectedTypes.includes('Deals') || selectedTypes.includes('Application'),
                Enterprise: selectedTypes.includes('Enterprise'),
                DarkWeb: selectedTypes.includes('DarkWeb'),
                Ransomware: selectedTypes.includes('Ransomware'),
                TPRM: selectedTypes.includes('TPRM'),
                AI: selectedTypes.includes('AI'),
                BusinessUnits: selectedTypes.includes('BusinessUnits'),
                GRC: selectedTypes.includes('GRC'),
                External: selectedTypes.includes('External'),
            };
        }
        // Default: all checked
        return {
            Deals: true,
            Enterprise: true,
            DarkWeb: true,
            Ransomware: true,
            TPRM: true,
            AI: true,
            BusinessUnits: true,
            GRC: true,
            External: true,
        };
    };

    const [pageSize, setPageSize] = usePageSize('remediations');
    const listTopRef = useRef(null);
    const [page, setPage] = useState(1);
    const [remediations, setRemediations] = useState([]);
    const [filteredRemediations, setFilteredRemediations] = useState([]);
    const pagedRemediations = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredRemediations.slice(start, start + pageSize);
    }, [filteredRemediations, page, pageSize]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('pending');
    const [filterPriority, setFilterPriority] = useState('all');
    const [filterAssignment, setFilterAssignment] = useState('all');
    const [filterFramework, setFilterFramework] = useState('all');
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [quickFilter, setQuickFilter] = useState('allPending');
    const [filterApplication, setFilterApplication] = useState('all');
    const [filterVendor, setFilterVendor] = useState('all');
    const [filterDeal, setFilterDeal] = useState('all');
    const [filterBusinessUnit, setFilterBusinessUnit] = useState('all');
    const [filterBUApplication, setFilterBUApplication] = useState('all');
    const [filterAllApplication, setFilterAllApplication] = useState('all');
    const [filterControlSource, setFilterControlSource] = useState('all');
    const [filterRiskCategory, setFilterRiskCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('targetDate');
    const [sortOrder, setSortOrder] = useState('asc');
    const [selectedRemediation, setSelectedRemediation] = useState(null);
    const [showManageModal, setShowManageModal] = useState(false);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showJiraModal, setShowJiraModal] = useState(false);
    const [selectedRemediationForJira, setSelectedRemediationForJira] = useState(null);
    const [showServiceNowModal, setShowServiceNowModal] = useState(false);
    const [selectedRemediationForServiceNow, setSelectedRemediationForServiceNow] = useState(null);
    // Jira / ServiceNow need internet access: their buttons are hidden where ticketing is unavailable.
    const ticketingAvailable = useModuleAvailability().isModuleAvailable('ticketing');
    const [users, setUsers] = useState([]);
    const [userMap, setUserMap] = useState({});
    const [filterTypes, setFilterTypes] = useState(getInitialFilterTypes);
    const [cardTabs, setCardTabs] = useState({});
    const skipFilterRef = useRef(false);
    // Custom workflows (Features:RemediationWorkflows, UI-only): only the expanded card mounts
    // WorkflowStepPanel + WorkflowTimeline. Flag off = no Workflow button, no section, deep link ignored.
    const remediationWorkflows = useRemediationWorkflows();
    const [expandedWorkflowId, setExpandedWorkflowId] = useState(null);
    // Id of the expanded section once both its children have loaded (see WorkflowSection).
    const [readyWorkflowId, setReadyWorkflowId] = useState(null);
    const [workflowRefreshTick, setWorkflowRefreshTick] = useState(0);
    const [scrollToRemediationId, setScrollToRemediationId] = useState(null);
    const cardRefs = useRef({});
    const deepLinkRemediationId = searchParams.get('remediationId');
    // True while a ?remediationId deep link is still being resolved: applyFilters must not reset
    // the page underneath it (the filter-reset effects re-run applyFilters several times after
    // the first fetch, which used to throw the target page away before the card rendered).
    const pendingDeepLinkRef = useRef(!!deepLinkRemediationId);
    const deepLinkWidenedRef = useRef(false);
    // Signature of the filter criteria the last applyFilters ran with: the page resets to 1 only
    // when a criterion changes, not when the data refreshes under the same criteria.
    const lastFilterSignatureRef = useRef(null);

    const expandWorkflow = (remediationId) => {
        setExpandedWorkflowId(remediationId);
        setReadyWorkflowId(null);
    };

    const toggleTypeFilter = (type) => {
        setFilterTypes(prev => ({ ...prev, [type]: !prev[type] }));
        setQuickFilter('none');
    };

    const applyQuickFilterValues = (value) => {
        const allTypes = {
            Deals: true, Enterprise: true, DarkWeb: true,
            Ransomware: true, TPRM: true, AI: true, BusinessUnits: true, GRC: true, External: true,
        };
        if (value === 'allPending') {
            setFilterStatus('pending');
            setFilterPriority('all');
            setFilterAssignment('all');
            setFilterFramework('all');
            setFilterDeal('all');
            setFilterApplication('all');
            setFilterVendor('all');
            setFilterBusinessUnit('all');
            setFilterBUApplication('all');
            setFilterAllApplication('all');
            setSearchTerm('');
            setFilterTypes(allTypes);
            setQuickFilter('allPending');
        } else if (value === 'myPending') {
            setFilterStatus('pending');
            setFilterPriority('all');
            setFilterAssignment('mine');
            setFilterFramework('all');
            setFilterDeal('all');
            setFilterApplication('all');
            setFilterVendor('all');
            setFilterBusinessUnit('all');
            setFilterBUApplication('all');
            setFilterAllApplication('all');
            setSearchTerm('');
            setFilterTypes(allTypes);
            setQuickFilter('myPending');
        } else if (value === 'needsAssignment') {
            setFilterStatus('all');
            setFilterPriority('all');
            setFilterAssignment('unassigned');
            setFilterFramework('all');
            setFilterDeal('all');
            setFilterApplication('all');
            setFilterVendor('all');
            setFilterBusinessUnit('all');
            setFilterBUApplication('all');
            setFilterAllApplication('all');
            setSearchTerm('');
            setFilterTypes(allTypes);
            setQuickFilter('needsAssignment');
        } else if (value === 'acceptedRisksMyApproval') {
            setFilterStatus('all');
            setFilterPriority('all');
            setFilterAssignment('all');
            setFilterFramework('all');
            setFilterDeal('all');
            setFilterApplication('all');
            setFilterVendor('all');
            setFilterBusinessUnit('all');
            setFilterBUApplication('all');
            setFilterAllApplication('all');
            setSearchTerm('');
            setFilterTypes(allTypes);
            setQuickFilter('acceptedRisksMyApproval');
        } else {
            setQuickFilter('none');
        }
    };

    // Unique deals from remediations (show name, filter by ID)
    const uniqueDeals = remediations
        .filter(r => r.dealID && r.dealName)
        .map(r => ({ dealID: r.dealID, dealName: r.dealName }))
        .filter((deal, index, self) =>
            index === self.findIndex(d => d.dealID === deal.dealID)
        )
        .sort((a, b) => (a.dealName || '').localeCompare(b.dealName || ''));

    // Unique applications - only show those matching selected deal
    const uniqueApplications = remediations
        .filter(r => filterDeal === 'all' || String(r.dealID) === String(filterDeal))
        .filter(r => r.applicationName)
        .map(r => r.applicationName)
        .filter((name, index, self) => self.indexOf(name) === index)
        .sort();

    // Unique vendors from all remediations (Deals, TPRM, and Business Units)
    const uniqueVendors = remediations
        .filter(r => r.vendorName)
        .map(r => r.vendorName)
        .filter((name, index, self) => self.indexOf(name) === index)
        .sort();

    // Unique business units from all remediations that have BU data
    const uniqueBusinessUnits = remediations
        .filter(r => r.businessUnitId && r.businessUnitName)
        .map(r => ({ businessUnitId: r.businessUnitId, businessUnitName: r.businessUnitName }))
        .filter((bu, index, self) =>
            index === self.findIndex(b => b.businessUnitId === bu.businessUnitId)
        )
        .sort((a, b) => (a.businessUnitName || '').localeCompare(b.businessUnitName || ''));

    // Unique BU applications - only show those matching selected business unit
    const uniqueBUApplications = remediations
        .filter(r => r.remediationType === 'BusinessUnits')
        .filter(r => filterBusinessUnit === 'all' || String(r.businessUnitId) === String(filterBusinessUnit))
        .filter(r => r.applicationName)
        .map(r => r.applicationName)
        .filter((name, index, self) => self.indexOf(name) === index)
        .sort();

    // Unique frameworks from all remediations
    const uniqueFrameworks = useMemo(() => {
        const frameworkSet = new Set();
        remediations.forEach(r => {
            if (r.affectedFrameworks) {
                r.affectedFrameworks.forEach(f => frameworkSet.add(f));
            }
        });
        return [...frameworkSet].sort();
    }, [remediations]);

    // Unique application names — scoped to selected vendor when one is chosen
    const uniqueAllApplications = [...new Set(
        remediations
            .filter(r => filterVendor === 'all' || r.vendorName === filterVendor)
            .filter(r => r.applicationName)
            .map(r => r.applicationName)
    )].sort();

    const uniqueControlSources = useMemo(() => {
        return [...new Set(remediations.filter(r => r.controlSource).map(r => r.controlSource))].sort();
    }, [remediations]);

    const uniqueRiskCategories = useMemo(() => {
        let source = remediations.filter(r => r.riskCategory);
        if (filterControlSource !== 'all') {
            source = source.filter(r => r.controlSource === filterControlSource);
        }
        return [...new Set(source.map(r => r.riskCategory))].sort();
    }, [remediations, filterControlSource]);

    // Initial fetch
    useEffect(() => {
        fetchRemediations();
        fetchUsers();
    }, []);

    useEffect(() => {
        if (skipFilterRef.current) {
            skipFilterRef.current = false;
            return;
        }
        applyFilters();
    }, [remediations, filterStatus, filterPriority, filterAssignment, filterFramework, filterApplication, filterVendor, filterDeal, filterBusinessUnit, filterBUApplication, filterAllApplication, filterControlSource, filterRiskCategory, filterTypes, searchTerm, sortBy, sortOrder, quickFilter]);

    // ?remediationId=<id> deep link: whenever the filtered list changes while the link is still
    // pending, page to the row, expand its Workflow section and scroll it into view. If the row is
    // filtered out (e.g. Completed under the default "pending" filter) widen the filters once; if
    // the id is unknown to the org, give up.
    useEffect(() => {
        if (!remediationWorkflows || loading || !pendingDeepLinkRef.current || !deepLinkRemediationId) return;
        // The filtered list fills one render after the fetch; wait for it unless there is nothing to filter.
        if (filteredRemediations.length === 0 && remediations.length > 0 && !deepLinkWidenedRef.current) return;
        const target = findDeepLinkTarget(filteredRemediations, deepLinkRemediationId, pageSize);
        if (!target) {
            const id = parseInt(deepLinkRemediationId, 10);
            const exists = remediations.some(r => r.remediationID === id);
            if (exists && !deepLinkWidenedRef.current) {
                deepLinkWidenedRef.current = true;
                setFilterStatus('all');
                setQuickFilter('none');
                return;
            }
            pendingDeepLinkRef.current = false;
            return;
        }
        setPage(target.page);
        if (expandedWorkflowId !== target.remediationID) expandWorkflow(target.remediationID);
        setScrollToRemediationId(target.remediationID);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remediationWorkflows, loading, deepLinkRemediationId, filteredRemediations, remediations, pageSize]);

    useEffect(() => {
        if (scrollToRemediationId == null) return;
        // The deep link expands the card's Workflow section: wait until that section is visible
        // (both children loaded) so the scroll lands on the final layout.
        if (expandedWorkflowId === scrollToRemediationId && readyWorkflowId !== scrollToRemediationId) return;
        const el = cardRefs.current[scrollToRemediationId];
        if (!el) return;
        if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setScrollToRemediationId(null);
        pendingDeepLinkRef.current = false;
    }, [scrollToRemediationId, pagedRemediations, expandedWorkflowId, readyWorkflowId]);

    // Reset application filter when deal changes
    useEffect(() => {
        setFilterApplication('all');
    }, [filterDeal]);

    // Reset BU application filter when business unit changes
    useEffect(() => {
        setFilterBUApplication('all');
    }, [filterBusinessUnit]);

    // Reset all-applications filter when vendor changes
    useEffect(() => { setFilterAllApplication('all'); }, [filterVendor]);

    // Set vendor filter when deal application changes
    useEffect(() => {
        if (filterApplication === 'all' || filterApplication === 'deal_only' || filterApplication === 'apps_only') {
            setFilterVendor('all');
        } else {
            // Find the vendor associated with this application
            const appRemediation = remediations.find(r => r.applicationName === filterApplication);
            if (appRemediation?.vendorName) {
                setFilterVendor(appRemediation.vendorName);
            } else {
                setFilterVendor('all');
            }
        }
    }, [filterApplication, remediations]);

    // Set vendor filter when BU application changes
    useEffect(() => {
        if (filterBUApplication === 'all') {
            // Only reset if deal application isn't also setting the vendor
            if (filterApplication === 'all') {
                setFilterVendor('all');
            }
        } else {
            const buRemediation = remediations.find(r => r.remediationType === 'BusinessUnits' && r.applicationName === filterBUApplication);
            if (buRemediation?.vendorName) {
                setFilterVendor(buRemediation.vendorName);
            } else {
                setFilterVendor('all');
            }
        }
    }, [filterBUApplication, remediations]);

    const [selectedRemIds, setSelectedRemIds]           = useState(new Set());
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);

    const toggleRemSelection = (id) =>
        setSelectedRemIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const toggleSelectAll = () =>
        setSelectedRemIds(prev =>
            prev.size === filteredRemediations.length && filteredRemediations.length > 0
                ? new Set()
                : new Set(filteredRemediations.map(r => r.remediationID))
        );

    const fetchRemediations = async () => {
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) {
                return;
            }
            const allRemediations = await apiCall("GET", "/allRemediations");
            setRemediations(allRemediations || []);
        } catch (error) {} finally {
            setLoading(false);
        }
    };


    const fetchUsers = async () => {
        try {
            const response = await apiCall('GET', '/users');
            setUsers(response || []);

            const mapping = {};
            (response || []).forEach(u => {
                mapping[String(u.id)] = u.fullName || u.email;
            });
            setUserMap(mapping);
        } catch (error) {}
    };

    const refreshSingleRemediation = async (remediationId) => {
        try {
            const allRemediations = await apiCall("GET", "/allRemediations");
            skipFilterRef.current = true;
            setRemediations(allRemediations || []);

            const updated = (allRemediations || []).find(r => r.remediationID === remediationId);
            if (updated) {
                setFilteredRemediations(prev => prev.map(r =>
                    r.remediationID === remediationId ? updated : r
                ));
            } else {
                // No longer returned by the (scope-filtered) backend list — e.g. a SELF-scope
                // contributor just reassigned this away and can no longer see it. Drop the stale
                // row from the view instead of leaving it behind.
                setFilteredRemediations(prev => prev.filter(r => r.remediationID !== remediationId));
            }
        } catch (error) {}
    };

    const applyFilters = () => {
        let filtered = [...remediations];

        // Filter by remediation type — match the primary remediationType OR any cross-listing tag
        // (e.g. an External Enzoic remediation tagged DarkWeb + Ransomware), gated to licensed buckets.
        filtered = filtered.filter(r =>
            filterTypes[r.remediationType] ||
            (r.tags || []).some(t => licensedFilterTypes.includes(t) && filterTypes[t]));

        // Hide accepted risks only when browsing the general pending/all view.
        // When the user explicitly picks a specific status (e.g. "Completed"), show everything
        // matching that status regardless of risk acceptance state.
        if (filterStatus === 'pending' || filterStatus === 'all') {
            filtered = filtered.filter(r => r.riskStatus !== 'Accepted');
        }

        // Quick filter: Accepted Risks requiring My Approval
        if (quickFilter === 'acceptedRisksMyApproval') {
            filtered = filtered.filter(r =>
                (r.riskStatus === 'AcceptancePendingPrimaryApproval' && String(r.primaryApproverUserId) === String(user.id)) ||
                (r.riskStatus === 'AcceptancePendingSecondaryApproval' && String(r.secondaryApproverUserId) === String(user.id))
            );
        }

        if (filterStatus === 'pending') {
            filtered = filtered.filter(r => r.remediationStatus !== 'Completed' && r.remediationStatus !== 'Deferred');
        } else if (filterStatus !== 'all') {
            filtered = filtered.filter(r => r.remediationStatus === filterStatus);
        }
        if (filterPriority !== 'all') filtered = filtered.filter(r => r.remediationPriority?.toUpperCase() === filterPriority.toUpperCase());

        if (filterAssignment === 'mine') filtered = filtered.filter(r => String(r.remediationAssignTo) === String(user.id));
        else if (filterAssignment === 'unassigned') filtered = filtered.filter(r => !r.remediationAssignTo);
        else if (filterAssignment !== 'all') filtered = filtered.filter(r => String(r.remediationAssignTo) === String(filterAssignment));

        if (filterFramework !== 'all') {
            filtered = filtered.filter(r =>
                r.affectedFrameworks && r.affectedFrameworks.includes(filterFramework)
            );
        }

        if (filterApplication === 'deal_only') {
            filtered = filtered.filter(r => !r.applicationName);
        } else if (filterApplication === 'apps_only') {
            filtered = filtered.filter(r => !!r.applicationName);
        } else if (filterApplication !== 'all') {
            filtered = filtered.filter(r => r.applicationName === filterApplication);
        }

        if (filterVendor !== 'all') {
            filtered = filtered.filter(r => r.vendorName === filterVendor);
        }

        if (filterDeal !== 'all') {
            filtered = filtered.filter(r => String(r.dealID) === String(filterDeal));
        }

        if (filterBusinessUnit !== 'all') {
            filtered = filtered.filter(r => String(r.businessUnitId) === String(filterBusinessUnit));
        }

        if (filterBUApplication !== 'all') {
            filtered = filtered.filter(r => r.remediationType === 'BusinessUnits' && r.applicationName === filterBUApplication);
        }

        if (filterAllApplication !== 'all') {
            filtered = filtered.filter(r => r.applicationName === filterAllApplication);
        }

        if (filterControlSource !== 'all') {
            filtered = filtered.filter(r => r.controlSource === filterControlSource);
        }

        if (filterRiskCategory !== 'all') {
            filtered = filtered.filter(r => r.riskCategory === filterRiskCategory);
        }

        if (searchTerm) {
            filtered = filtered.filter(r =>
                (r.remediationTitle && r.remediationTitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (r.actionDescription && r.actionDescription.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (r.applicationName && r.applicationName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (r.dealName && r.dealName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (r.riskName && r.riskName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (r.businessUnitName && r.businessUnitName.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        filtered.sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (sortBy === 'targetDate') {
                aVal = aVal ? new Date(aVal) : new Date('9999-12-31');
                bVal = bVal ? new Date(bVal) : new Date('9999-12-31');
            }

            if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
            else return aVal < bVal ? 1 : -1;
        });

        setFilteredRemediations(filtered);
        const signature = JSON.stringify([filterStatus, filterPriority, filterAssignment, filterFramework, filterApplication, filterVendor, filterDeal, filterBusinessUnit, filterBUApplication, filterAllApplication, filterControlSource, filterRiskCategory, filterTypes, searchTerm, sortBy, sortOrder, quickFilter]);
        const criteriaChanged = lastFilterSignatureRef.current !== null && lastFilterSignatureRef.current !== signature;
        lastFilterSignatureRef.current = signature;
        if (criteriaChanged && !pendingDeepLinkRef.current) setPage(1);
    };

    const getPriorityColor = (priority) => {
        switch ((priority || '').toUpperCase()) {
            case 'CRITICAL':
                return 'text-red-700 bg-red-100 border-red-300';
            case 'HIGH':
                return 'text-orange-600 bg-orange-100 border-orange-300';
            case 'MEDIUM':
                return 'text-yellow-600 bg-yellow-100 border-yellow-300';
            case 'LOW':
                return 'text-green-600 bg-green-100 border-green-300';
            default:
                return 'text-gray-600 bg-gray-100 border-gray-300';
        }
    };

    const formatPriorityLabel = (priority) => {
        if (!priority) return '';
        return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed':
                return 'text-green-600 bg-green-100 border-green-300';
            case 'Planned':
                return 'text-yellow-600 bg-yellow-100 border-yellow-300';
            case 'In Progress':
                return 'text-blue-600 bg-blue-100 border-blue-300';
            case 'Under Review':
                return 'text-purple-600 bg-purple-100 border-purple-300';
            case 'Deferred':
                return 'text-gray-600 bg-gray-100 border-gray-300';
            default:
                return 'text-gray-600 bg-gray-100 border-gray-300';
        }
    };

    const isOverdue = (dueDate, status) => {
        if (!dueDate || status === 'Completed' || status === 'Deferred') return false;
        return new Date(dueDate) < new Date();
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'Deals':
                return 'text-blue-600 bg-blue-100 border-blue-300';
            case 'DarkWeb':
                return 'text-purple-600 bg-purple-100 border-purple-300';
            case 'Ransomware':
                return 'text-red-600 bg-red-100 border-red-300';
            case 'Enterprise':
                return 'text-green-600 bg-green-100 border-green-300';
            case 'TPRM':
                return 'text-teal-600 bg-teal-100 border-teal-300';
            case 'AI':
                return 'text-amber-600 bg-amber-100 border-amber-300';
            case 'BusinessUnits':
                return 'text-indigo-600 bg-indigo-100 border-indigo-300';
            case 'GRC':
                return 'text-rose-600 bg-rose-100 border-rose-300';
            case 'External':
                return 'text-cyan-600 bg-cyan-100 border-cyan-300';
            default:
                return 'text-gray-600 bg-gray-100 border-gray-300';
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'Deals':
                return 'Deals';
            case 'DarkWeb':
                return 'Dark Web';
            case 'Ransomware':
                return 'Ransomware';
            case 'Enterprise':
                return 'Enterprise';
            case 'TPRM':
                return 'TPRM';
            case 'AI':
                return 'AI';
            case 'BusinessUnits':
                return 'Business Units';
            case 'GRC':
                return 'GRC';
            case 'External':
                return 'External';
            default:
                return type || 'Unknown';
        }
    };

    const getRiskEndpoints = (remediation) => {
        const riskId = remediation.riskId;
        // All risks live in UnifiedRisks; route all mutations to the unified controller (keyed by the GUID).
        return { update: `/unifiedrisks/${riskId}/update-status`, accept: `/unifiedrisks/${riskId}/accept`, assign: `/unifiedrisks/${riskId}/assign`, approveBase: `/unifiedrisks/${riskId}`, fieldPrefix: '' };
    };

    const buildRiskObject = (remediation) => ({
        riskId: remediation.riskId,
        riskTitle: remediation.riskName || remediation.remediationTitle,
        detailedRiskDescription: remediation.riskDescription,
        remediationStatus: remediation.remediationStatus,
        remediationTargetDate: remediation.targetDate,
        userRating: remediation.remediationPriority,
        riskName: remediation.riskName || remediation.remediationTitle,
        progressPercentage: remediation.progressPercentage,
        remediationID: remediation.remediationID,
        remediationType: remediation.remediationType,
        remediationAssignTo: remediation.remediationAssignTo,
        primaryApprovalStatus: remediation.primaryApprovalStatus,
        primaryApproverUserId: remediation.primaryApproverUserId,
        secondaryApprovalStatus: remediation.secondaryApprovalStatus,
        secondaryApproverUserId: remediation.secondaryApproverUserId,
        remediation_plan: remediation.actionDescription,
        dealId: remediation.dealID || null,
        businessUnitId: remediation.businessUnitId || null,
    });

    const getAssessmentDescription = (remediation) => {
        const typeLabel = {
            'Deals': '',
            'Enterprise': 'Enterprise',
            'Ransomware': 'Ransomware',
            'DarkWeb': 'DarkWeb',
            'TPRM': 'TPRM',
            'AI': 'AI',
            'BusinessUnits': '',
            'GRC': 'GRC',
        }[remediation.remediationType] || '';

        if (remediation.dealName) {
            return `Deal ${remediation.dealName}${typeLabel ? ' ' + typeLabel : ''} Assessment`;
        }
        if (remediation.businessUnitName) {
            return `Business Unit ${remediation.businessUnitName}${typeLabel ? ' ' + typeLabel : ''} Assessment`;
        }
        return `${typeLabel} Organization Assessment`.trim();
    };

    const canApproveRisk = (remediation) => {
        return (remediation.primaryApprovalStatus === 1 && remediation.primaryApproverUserId === user?.id) ||
            (remediation.secondaryApprovalStatus === 1 && remediation.secondaryApproverUserId === user?.id);
    };

    const handleServiceNowIntegration = (remediation) => {
        // Map the remediation type to the API type parameter
        const typeMap = {
            'Deals': 'application',
            'DarkWeb': 'darkweb',
            'Ransomware': 'ransomware',
            'Enterprise': 'enterprise',
            'TPRM': 'tprm',
            'AI': 'ai',
            'BusinessUnits': 'businessunits',
            'GRC': 'grc',
            'External': 'external',
        };
        const remediationType = typeMap[remediation.remediationType] || 'application';
        setSelectedRemediationForServiceNow({ ...remediation, remediationType });
        setShowServiceNowModal(true);
    };

    const handleJiraIntegration = (remediation) => {
        // Map the remediation type to the API type parameter
        const typeMap = {
            'Deals': 'application',
            'DarkWeb': 'darkweb',
            'Ransomware': 'ransomware',
            'Enterprise': 'enterprise',
            'TPRM': 'tprm',
            'AI': 'ai',
            'BusinessUnits': 'businessunits',
            'GRC': 'grc',
            'External': 'external',
        };
        const remediationType = typeMap[remediation.remediationType] || 'application';
        setSelectedRemediationForJira({ ...remediation, remediationType });
        setShowJiraModal(true);
    };

    const handleJiraSuccess = (result) => {
        // Refresh remediations to show the linked Jira issue
        refreshSingleRemediation(selectedRemediationForJira?.remediationID);
    };

    const getJiraIssueKey = (remediation) => {
        return remediation.jiraIssueKey;
    };

    const handleServiceNowSuccess = (result) => {
        // Refresh remediations to show the linked ServiceNow incident
        refreshSingleRemediation(selectedRemediationForServiceNow?.remediationID);
    };

    const getServiceNowIncidentNumber = (remediation) => {
        return remediation.serviceNowIncidentNumber;
    };

    const totalPages = Math.max(1, Math.ceil(filteredRemediations.length / pageSize));
    // Keep the page in range when the list shrinks under the same criteria (page no longer resets on refresh).
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    if (loading) return <LoadingSpinner />;

    return (
        <div className="min-h-screen bg-theme-primary">
            <Navigation />
            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold text-content-primary mb-2">Remediation Tracker</h1>
                <p className="text-gray-600 mb-6">Track and manage security remediation tasks</p>

                {/* Quick Filters — always visible */}
                <div className="bg-white p-4 rounded-lg shadow mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Quick Filters</h3>
                    <div className="flex items-center space-x-6">
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="quickFilter"
                                checked={quickFilter === 'allPending'}
                                onChange={() => applyQuickFilterValues(quickFilter === 'allPending' ? 'none' : 'allPending')}
                                className="h-4 w-4 text-blue-600 border-gray-300"
                            />
                            <span className="text-sm text-gray-700">All Pending Remediations</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="quickFilter"
                                checked={quickFilter === 'myPending'}
                                onChange={() => applyQuickFilterValues(quickFilter === 'myPending' ? 'none' : 'myPending')}
                                className="h-4 w-4 text-blue-600 border-gray-300"
                            />
                            <span className="text-sm text-gray-700">My Pending Remediations</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="quickFilter"
                                checked={quickFilter === 'needsAssignment'}
                                onChange={() => applyQuickFilterValues(quickFilter === 'needsAssignment' ? 'none' : 'needsAssignment')}
                                className="h-4 w-4 text-blue-600 border-gray-300"
                            />
                            <span className="text-sm text-gray-700">Needs Assignment</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="quickFilter"
                                checked={quickFilter === 'acceptedRisksMyApproval'}
                                onChange={() => applyQuickFilterValues(quickFilter === 'acceptedRisksMyApproval' ? 'none' : 'acceptedRisksMyApproval')}
                                className="h-4 w-4 text-blue-600 border-gray-300"
                            />
                            <span className="text-sm text-gray-700">Accepted Risks requiring My Approval</span>
                        </label>
                    </div>
                </div>

                {/* All Filters — collapsible, collapsed by default */}
                <div className="bg-white rounded-lg shadow mb-4">
                    <button
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                        className="w-full px-4 py-2 flex items-center justify-between text-left"
                    >
                        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <Filter className="h-4 w-4" />
                            All Filters
                        </span>
                        {filtersExpanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                    </button>
                    {filtersExpanded && (
                        <div className="px-4 py-3 border-t">
                            {/* Type Checkboxes */}
                            <div className="flex items-center flex-wrap gap-x-6 gap-y-2 mb-3">
                                <span className="text-sm font-medium text-gray-700">Type:</span>
                                {(() => {
                                    const allChecked = licensedFilterTypes.every(t => filterTypes[t]);
                                    return (
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={allChecked}
                                                onChange={() => {
                                                    const next = !allChecked;
                                                    setFilterTypes(prev => {
                                                        const updated = { ...prev };
                                                        licensedFilterTypes.forEach(t => { updated[t] = next; });
                                                        return updated;
                                                    });
                                                    setQuickFilter('none');
                                                }}
                                                className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                            />
                                            <span className="text-sm font-medium text-gray-500">All</span>
                                        </label>
                                    );
                                })()}
                                {licensedFilterTypes.map(type => (
                                    <label key={type} className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={filterTypes[type]}
                                            onChange={() => toggleTypeFilter(type)}
                                            className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                        />
                                        <span className="text-sm text-gray-700">{getTypeLabel(type)}</span>
                                    </label>
                                ))}
                            </div>
                            {/* Row 1: Status, Priority */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                {/* Status Filter */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => { setFilterStatus(e.target.value); setQuickFilter('none'); }}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="pending">Pending Statuses</option>
                                        <option value="all">All Statuses</option>
                                        <option value="Not Started">Not Started</option>
                                        <option value="Planned">Planned</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Under Review">Under Review</option>
                                        <option value="Completed">Completed</option>
                                    </select>
                                </div>

                                {/* Priority Filter */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                                    <select
                                        value={filterPriority}
                                        onChange={(e) => { setFilterPriority(e.target.value); setQuickFilter('none'); }}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Priorities</option>
                                        <option value="CRITICAL">Critical</option>
                                        <option value="HIGH">High</option>
                                        <option value="MEDIUM">Medium</option>
                                        <option value="LOW">Low</option>
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: Assigned To, Framework */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                {/* Assigned To Filter */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Assigned To</label>
                                    <select
                                        value={filterAssignment}
                                        onChange={(e) => { setFilterAssignment(e.target.value); setQuickFilter('none'); }}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All</option>
                                        <option value="unassigned">Unassigned</option>
                                        <option value="mine">Assigned to Me</option>
                                        {users
                                            .slice()
                                            .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))
                                            .map(u => (
                                                <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                                            ))}
                                    </select>
                                </div>

                                {/* Framework Filter */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Framework</label>
                                    <select
                                        value={filterFramework}
                                        onChange={(e) => { setFilterFramework(e.target.value); setQuickFilter('none'); }}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Frameworks</option>
                                        {uniqueFrameworks.map((framework) => (
                                            <option key={framework} value={framework}>
                                                {framework}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: Deals, Deal Applications */}
                            {hasLicense('Deals') && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                    {/* Deal Filter*/}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Deal</label>
                                        <select
                                            value={filterDeal}
                                            onChange={(e) => { setFilterDeal(e.target.value); setQuickFilter('none'); }}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                        >
                                            <option value="all">All Deals</option>
                                            {uniqueDeals.map((deal) => (
                                                <option key={deal.dealID} value={deal.dealID}>
                                                    {deal.dealName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Deal Application Filter */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Deal Application</label>
                                        <select
                                            value={filterApplication}
                                            onChange={(e) => { setFilterApplication(e.target.value); setQuickFilter('none'); }}
                                            disabled={filterDeal === 'all'}
                                            className={`w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm ${filterDeal === 'all' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        >
                                            {filterDeal === 'all' ? (
                                                <option value="all">-- Select a Deal --</option>
                                            ) : (
                                                <>
                                                    <option value="deal_only">Only Deal - No Application</option>
                                                    <option value="all">Deals + All Applications</option>
                                                    <option value="apps_only">All Applications</option>
                                                    {uniqueApplications.map((app) => (
                                                        <option key={app} value={app}>{app}</option>
                                                    ))}
                                                </>
                                            )}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Row 3: Business Units, Business Unit Applications */}
                            {hasLicense('BusinessUnits') && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                    {/* Business Unit Filter */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Business Unit</label>
                                        <select
                                            value={filterBusinessUnit}
                                            onChange={(e) => { setFilterBusinessUnit(e.target.value); setQuickFilter('none'); }}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                        >
                                            <option value="all">All Business Units</option>
                                            {uniqueBusinessUnits.map((bu) => (
                                                <option key={bu.businessUnitId} value={bu.businessUnitId}>
                                                    {bu.businessUnitName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Business Unit Application Filter */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Business Unit Application</label>
                                        <select
                                            value={filterBUApplication}
                                            onChange={(e) => { setFilterBUApplication(e.target.value); setQuickFilter('none'); }}
                                            disabled={filterBusinessUnit === 'all'}
                                            className={`w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm ${filterBusinessUnit === 'all' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        >
                                            <option value="all">{filterBusinessUnit === 'all' ? '-- Select a Business Unit --' : 'All Applications'}</option>
                                            {uniqueBUApplications.map((app) => (
                                                <option key={app} value={app}>
                                                    {app}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Row 4: Vendor, All Applications */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Vendor</label>
                                    <select
                                        value={filterVendor}
                                        onChange={(e) => { setFilterVendor(e.target.value); setQuickFilter('none'); }}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Vendors</option>
                                        {uniqueVendors.map((vendor) => (
                                            <option key={vendor} value={vendor}>
                                                {vendor}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* All Applications */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">All Applications</label>
                                    <select
                                        value={filterAllApplication}
                                        onChange={(e) => { setFilterAllApplication(e.target.value); setQuickFilter('none'); }}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Applications</option>
                                        {uniqueAllApplications.map((app) => (
                                            <option key={app} value={app}>
                                                {app}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Row: GRC Control Source, GRC Risk Category */}
                            {hasLicense('GRC') && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">GRC Control Source</label>
                                        <select
                                            value={filterControlSource}
                                            onChange={(e) => { setFilterControlSource(e.target.value); setFilterRiskCategory('all'); setQuickFilter('none'); }}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                        >
                                            <option value="all">All Control Sources</option>
                                            {uniqueControlSources.map(s => (<option key={s} value={s}>{s === "RansomwareLiveMonitoring" ? "Ransomware Monitoring" : s}</option>))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">GRC Risk Category</label>
                                        <select
                                            value={filterRiskCategory}
                                            onChange={(e) => { setFilterRiskCategory(e.target.value); setQuickFilter('none'); }}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                        >
                                            <option value="all">All Risk Categories</option>
                                            {uniqueRiskCategories.map(c => (<option key={c} value={c}>{c}</option>))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Reset button */}
                            <div className="flex justify-end">
                                <button
                                    onClick={() => applyQuickFilterValues('allPending')}
                                    className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 transition-colors"
                                >
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Search + Sort bar */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className="relative flex-1 min-w-48">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search remediations..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setQuickFilter('none'); }}
                            className="pl-9 w-full border border-gray-300 rounded-md py-2 text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Sort by:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                        >
                            <option value="targetDate">Due Date</option>
                            <option value="remediationPriority">Priority</option>
                            <option value="remediationStatus">Status</option>
                            <option value="createdAt">Created Date</option>
                        </select>
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="w-28 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                        >
                            <option value="asc">Ascending</option>
                            <option value="desc">Descending</option>
                        </select>
                    </div>
                </div>

                {/* Bulk assign action bar */}
                {selectedRemIds.size > 0 && (
                    <div className={`sticky ${PINNED_PANEL_TOP} z-20 flex items-center gap-3 mb-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg shadow-sm`}>
                        <span className="text-sm font-medium text-blue-800">
                            {selectedRemIds.size} remediation{selectedRemIds.size !== 1 ? 's' : ''} selected
                        </span>
                        <button
                            onClick={() => setShowBulkAssignModal(true)}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center gap-1.5"
                        >
                            Assign Selected
                        </button>
                        <button onClick={() => setSelectedRemIds(new Set())} className="text-sm text-blue-600 hover:text-blue-800">Clear</button>
                    </div>
                )}
                {/* Result count + Select All */}
                <div ref={listTopRef} className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-500">
                        Showing {pagedRemediations.length} of {filteredRemediations.length} remediations
                        {filteredRemediations.length !== remediations.length && ` (filtered from ${remediations.length} total)`}
                    </span>
                    {filteredRemediations.length > 0 && (
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 rounded border-gray-300 cursor-pointer"
                                checked={selectedRemIds.size === filteredRemediations.length && filteredRemediations.length > 0}
                                ref={el => { if (el) el.indeterminate = selectedRemIds.size > 0 && selectedRemIds.size < filteredRemediations.length; }}
                                onChange={toggleSelectAll}
                            />
                            <span className="text-sm text-gray-600">Select All</span>
                        </label>
                    )}
                </div>
                {/* Remediation List */}
                <div className="space-y-4">
                    {filteredRemediations.length === 0 ? (
                        <div className="text-center py-12">
                            <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-content-primary">No remediations found</h3>
                        </div>
                    ) : (
                        pagedRemediations.map(remediation => (
                            <div
                                key={remediation.remediationID}
                                ref={el => { if (el) cardRefs.current[remediation.remediationID] = el; else delete cardRefs.current[remediation.remediationID]; }}
                                className="bg-white rounded-lg shadow p-6"
                                data-testid={`remediation-card-${remediation.remediationID}`}
                            >
                                {canManageRisk(remediation) && (
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 mb-3 text-blue-600 rounded border-gray-300 cursor-pointer"
                                        checked={selectedRemIds.has(remediation.remediationID)}
                                        onChange={e => { e.stopPropagation(); toggleRemSelection(remediation.remediationID); }}
                                    />
                                )}
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <h3 className="text-lg font-semibold text-content-primary">{remediation.remediationTitle}</h3>
                                    <span
                                        className={`px-3 py-1 rounded-full text-sm font-medium border ${getTypeColor(remediation.remediationType)}`}>{getTypeLabel(remediation.remediationType)}</span>
                                    {/* Cross-listing tags (e.g. an External Enzoic remediation tagged DarkWeb + Ransomware) */}
                                    {(remediation.tags || []).filter(t => t !== remediation.remediationType).map(t => (
                                        <span key={t}
                                            className={`px-3 py-1 rounded-full text-sm font-medium border ${getTypeColor(t)}`}>{getTypeLabel(t)}</span>
                                    ))}
                                    <span
                                        className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(remediation.remediationPriority)}`}>{formatPriorityLabel(remediation.remediationPriority)}</span>
                                    <span
                                        className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(remediation.remediationStatus)}`}>{remediation.remediationStatus}</span>
                                    {isOverdue(remediation.targetDate, remediation.remediationStatus) && (
                                        <span
                                            className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-600 border border-red-300">Overdue</span>
                                    )}
                                </div>
                                {remediation.targetDate && (
                                    <p className="text-xs text-gray-600 mb-2">
                                        <span className="font-medium text-gray-700">ETA:</span>{" "}
                                        {new Date(remediation.targetDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                                    </p>
                                )}
                                <p className="text-sm text-gray-600 mb-3">{getAssessmentDescription(remediation)}</p>
                                {/* Info / Activity Log tabs */}
                                <div className="mb-3">
                                    {/* Chrome tab bar */}
                                    <div className="flex items-end gap-0.5 bg-slate-100 border border-gray-200 border-b-0 rounded-t-xl px-2 pt-2">
                                        <button
                                            onClick={() => setCardTabs(prev => ({ ...prev, [remediation.remediationID]: 'info' }))}
                                            className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                                                (cardTabs[remediation.remediationID] || 'info') === 'info'
                                                    ? 'bg-white text-indigo-700 border-gray-200 z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]'
                                                    : 'bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
                                            }`}
                                            style={(cardTabs[remediation.remediationID] || 'info') === 'info' ? { borderBottomColor: '#ffffff' } : {}}
                                        >
                                            Info
                                        </button>
                                        <button
                                            onClick={() => setCardTabs(prev => ({ ...prev, [remediation.remediationID]: 'log' }))}
                                            className={`relative flex items-center gap-2 px-5 py-2.5 text-xs font-semibold rounded-t-lg -mb-px transition-all duration-150 focus:outline-none border ${
                                                (cardTabs[remediation.remediationID] || 'info') === 'log'
                                                    ? 'bg-white text-indigo-700 border-gray-200 z-10 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]'
                                                    : 'bg-transparent border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
                                            }`}
                                            style={(cardTabs[remediation.remediationID] || 'info') === 'log' ? { borderBottomColor: '#ffffff' } : {}}
                                        >
                                            Activity Log
                                        </button>
                                        <div className="flex-1 border-b border-gray-200 self-end h-[1px]" />
                                    </div>
                                    {/* Tab content panel */}
                                    <div className="border border-gray-200 border-t-0 rounded-b-xl rounded-tr-xl bg-white p-4 mb-1">
                                        {/* Tab 1: Info */}
                                        {(cardTabs[remediation.remediationID] || 'info') === 'info' && (
                                            <>
                                                {remediation.riskDescription && (
                                                    <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                        <h4 className="font-semibold text-gray-700 mb-1 text-sm">Risk Description</h4>
                                                        <p className="text-gray-700 text-sm">{remediation.riskDescription}</p>
                                                    </div>
                                                )}
                                                {(() => {
                                                    const rawSteps = remediation.riskRemediationSteps;
                                                    if (!rawSteps) return null;
                                                    const cleanedSteps = parseRemediationSteps(rawSteps);
                                                    const stepsContent = !cleanedSteps ? (
                                                        <p className="text-sm text-gray-700">{rawSteps}</p>
                                                    ) : cleanedSteps.map((step, i) => (
                                                        <div key={i} className="mb-1 text-sm text-gray-700">{i + 1}. {step}</div>
                                                    ));
                                                    return (
                                                        <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                                            <h4 className="font-semibold text-blue-900 mb-1 text-sm">Remediation Steps</h4>
                                                            {stepsContent}
                                                        </div>
                                                    );
                                                })()}
                                                {remediation.riskRemediationTrigger && (
                                                    <div className="mb-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                                        <h4 className="font-semibold text-purple-900 mb-1 text-sm">Remediation Trigger</h4>
                                                        <p className="text-sm text-gray-700 break-words">{remediation.riskRemediationTrigger}</p>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Tab 2: Activity Log */}
                                        {(cardTabs[remediation.remediationID] || 'info') === 'log' && (
                                            <ActivityLogTab
                                                riskId={remediation.riskId}
                                                riskType={remediation.remediationType}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Assigned To */}
                                <div className="flex items-center mt-3 pt-3 border-t text-sm text-gray-600">
                                    <User className="h-4 w-4 mr-1" />
                                    <span className="font-medium mr-1">Assigned to:</span>
                                    {remediation.remediationAssignTo
                                        ? (remediation.remediationAssignToName || userMap[remediation.remediationAssignTo] || `User #${remediation.remediationAssignTo}`)
                                        : <span className="italic text-gray-400">Unassigned</span>
                                    }
                                </div>
                                {remediation.riskStatus === 'AcceptancePendingPrimaryApproval' && remediation.primaryApproverUserId && (
                                    <div className="flex items-center mt-1 text-sm text-amber-600">
                                        <span className="font-medium mr-1">Waiting on Approval from:</span>
                                        {remediation.primaryApproverName || userMap[String(remediation.primaryApproverUserId)] || `User #${remediation.primaryApproverUserId}`}
                                    </div>
                                )}
                                {remediation.riskStatus === 'AcceptancePendingSecondaryApproval' && remediation.secondaryApproverUserId && (
                                    <div className="flex items-center mt-1 text-sm text-amber-600">
                                        <span className="font-medium mr-1">Waiting on Approval from:</span>
                                        {remediation.secondaryApproverName || userMap[String(remediation.secondaryApproverUserId)] || `User #${remediation.secondaryApproverUserId}`}
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-2 mt-2">
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => navigate(`/risks?riskId=${remediation.riskId}`)}
                                    >
                                        <Eye className="h-4 w-4 mr-2" />
                                        View Risk
                                    </Button>

                                    {remediationWorkflows && (
                                        <Button
                                            size="sm"
                                            variant={expandedWorkflowId === remediation.remediationID ? 'secondary' : 'primary'}
                                            aria-expanded={expandedWorkflowId === remediation.remediationID}
                                            aria-controls={`workflow-section-${remediation.remediationID}`}
                                            onClick={() => expandWorkflow(expandedWorkflowId === remediation.remediationID ? null : remediation.remediationID)}
                                        >
                                            <Workflow className="h-4 w-4 mr-2" />
                                            {expandedWorkflowId === remediation.remediationID ? 'Hide workflow' : 'Workflow'}
                                        </Button>
                                    )}

                                    {canUpdateRisk(remediation) && (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={selectedRemIds.size > 0}
                                            title={selectedRemIds.size > 0 ? 'Clear or assign the current selection first' : undefined}
                                            onClick={() => {
                                                setSelectedRemediation(remediation);
                                                setShowManageModal(true);
                                            }}
                                        >
                                            <Edit className="h-4 w-4 mr-2" />
                                            Manage
                                        </Button>
                                    )}

                                    {ticketingAvailable && canManageRisk(remediation) && (
                                    <Button
                                        size="sm"
                                        variant={getServiceNowIncidentNumber(remediation) ? 'success' : 'primary'}
                                        disabled={selectedRemIds.size > 0}
                                        title={selectedRemIds.size > 0 ? 'Clear or assign the current selection first' : undefined}
                                        onClick={() => handleServiceNowIntegration(remediation)}
                                    >
                                        {getServiceNowIncidentNumber(remediation) ? (
                                            <>
                                                <ExternalLink className="h-4 w-4 mr-2" />
                                                {getServiceNowIncidentNumber(remediation)}
                                            </>
                                        ) : (
                                            <>
                                                <Link2 className="h-4 w-4 mr-2" />
                                                ServiceNow
                                            </>
                                        )}
                                    </Button>
                                    )}

                                    {ticketingAvailable && canManageRisk(remediation) && (
                                    <Button
                                        size="sm"
                                        variant={getJiraIssueKey(remediation) ? 'success' : 'primary'}
                                        disabled={selectedRemIds.size > 0}
                                        title={selectedRemIds.size > 0 ? 'Clear or assign the current selection first' : undefined}
                                        onClick={() => handleJiraIntegration(remediation)}
                                    >
                                        {getJiraIssueKey(remediation) ? (
                                            <>
                                                <ExternalLink className="h-4 w-4 mr-2" />
                                                {getJiraIssueKey(remediation)}
                                            </>
                                        ) : (
                                            <>
                                                <Link2 className="h-4 w-4 mr-2" />
                                                Jira
                                            </>
                                        )}
                                    </Button>
                                    )}

                                    {canUpdateRisk(remediation) && (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={selectedRemIds.size > 0}
                                            title={selectedRemIds.size > 0 ? 'Clear or assign the current selection first' : undefined}
                                            onClick={() => {
                                                setSelectedRemediation(remediation);
                                                setShowAssignModal(true);
                                            }}
                                        >
                                            <Shield className="h-4 w-4 mr-2" />
                                            {remediation.remediationAssignTo ? 'Reassign' : 'Assign'}
                                        </Button>
                                    )}

                                    {canUpdateRisk(remediation) && (
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            disabled={
                                                selectedRemIds.size > 0
                                                || ['Accepted', 'AcceptancePendingPrimaryApproval', 'AcceptancePendingSecondaryApproval'].includes(remediation.riskStatus)
                                                || remediation.remediationStatus === 'Completed'
                                            }
                                            title={selectedRemIds.size > 0 ? 'Clear or assign the current selection first' : undefined}
                                            onClick={() => {
                                                setSelectedRemediation(remediation);
                                                setShowAcceptModal(true);
                                            }}
                                        >
                                            <Shield className="h-4 w-4 mr-2" />
                                            Accept Risk
                                        </Button>
                                    )}

                                    {canApproveRisk(remediation) && (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={selectedRemIds.size > 0}
                                            title={selectedRemIds.size > 0 ? 'Clear or assign the current selection first' : undefined}
                                            onClick={() => {
                                                setSelectedRemediation(remediation);
                                                setShowApprovalModal(true);
                                            }}
                                        >
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                            Approve
                                        </Button>
                                    )}
                                </div>

                                {/* Workflow section: step panel + timeline, mounted only for the expanded card */}
                                {remediationWorkflows && expandedWorkflowId === remediation.remediationID && (
                                    <WorkflowSection
                                        remediation={remediation}
                                        refreshKey={workflowRefreshTick}
                                        onChanged={() => {
                                            setWorkflowRefreshTick(t => t + 1);
                                            refreshSingleRemediation(remediation.remediationID);
                                        }}
                                        onClose={() => expandWorkflow(null)}
                                        onReady={setReadyWorkflowId}
                                    />
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination Controls */}
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    totalItems={filteredRemediations.length}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    scrollTargetRef={listTopRef}
                />
            </div>

            {/* Manage Risk Modal */}
            {showManageModal && selectedRemediation && (() => {
                const endpoints = getRiskEndpoints(selectedRemediation);
                return (
                    <RiskManagementModal
                        key={selectedRemediation.remediationID}
                        risk={buildRiskObject(selectedRemediation)}
                        updateEndpoint={endpoints.update}
                        fieldPrefix={endpoints.fieldPrefix}
                        onClose={() => {
                            setShowManageModal(false);
                            setSelectedRemediation(null);
                        }}
                        refreshData={() => refreshSingleRemediation(selectedRemediation.remediationID)}
                    />
                );
            })()}

            {/* Accept Risk Modal */}
            {showAcceptModal && selectedRemediation && (() => {
                const endpoints = getRiskEndpoints(selectedRemediation);
                return (
                    <RiskAcceptanceModal
                        show={showAcceptModal}
                        risk={buildRiskObject(selectedRemediation)}
                        acceptEndpoint={endpoints.accept}
                        fieldPrefix={endpoints.fieldPrefix}
                        onClose={() => {
                            setShowAcceptModal(false);
                            setSelectedRemediation(null);
                        }}
                        onAccepted={() => refreshSingleRemediation(selectedRemediation.remediationID)}
                        riskId={selectedRemediation.riskId}
                        riskType={selectedRemediation.remediationType}
                    />
                );
            })()}

            {/* Assign Risk Modal */}
            {showAssignModal && selectedRemediation && (() => {
                const endpoints = getRiskEndpoints(selectedRemediation);
                return (
                    <AssignRiskModal
                        show={showAssignModal}
                        risk={buildRiskObject(selectedRemediation)}
                        assignEndpoint={endpoints.assign}
                        onClose={() => {
                            setShowAssignModal(false);
                            setSelectedRemediation(null);
                        }}
                        refreshData={() => refreshSingleRemediation(selectedRemediation.remediationID)}
                        riskId={selectedRemediation.riskId}
                        riskType={selectedRemediation.remediationType}
                        dealId={selectedRemediation.dealID || undefined}
                    />
                );
            })()}

            {/* Approve Risk Modal */}
            {showApprovalModal && selectedRemediation && (() => {
                const endpoints = getRiskEndpoints(selectedRemediation);
                return (
                    <RiskApprovalModal
                        show={showApprovalModal}
                        risk={buildRiskObject(selectedRemediation)}
                        approveBaseEndpoint={endpoints.approveBase}
                        onClose={() => {
                            setShowApprovalModal(false);
                            setSelectedRemediation(null);
                        }}
                        refreshData={() => refreshSingleRemediation(selectedRemediation.remediationID)}
                        riskId={selectedRemediation.riskId}
                        riskType={selectedRemediation.remediationType}
                    />
                );
            })()}

            {/* Jira Integration Modal */}
            {showJiraModal && (
                <CreateJiraIssueModal
                    isOpen={showJiraModal}
                    onClose={() => {
                        setShowJiraModal(false);
                        setSelectedRemediationForJira(null);
                    }}
                    remediation={selectedRemediationForJira}
                    remediationType={selectedRemediationForJira?.remediationType}
                    onSuccess={handleJiraSuccess}
                />
            )}

            {/* ServiceNow Integration Modal */}
            {showServiceNowModal && (
                <CreateServiceNowIncidentModal
                    isOpen={showServiceNowModal}
                    onClose={() => {
                        setShowServiceNowModal(false);
                        setSelectedRemediationForServiceNow(null);
                    }}
                    remediation={selectedRemediationForServiceNow}
                    remediationType={selectedRemediationForServiceNow?.remediationType}
                    onSuccess={handleServiceNowSuccess}
                />
            )}

            {showBulkAssignModal && (() => {
                const selected = filteredRemediations.filter(r => selectedRemIds.has(r.remediationID));
                // Deal context only when every selected remediation belongs to the same deal
                const dealIds = new Set(selected.map(r => r.dealID || null));
                const sharedDealId = dealIds.size === 1 ? [...dealIds][0] || undefined : undefined;
                return (
                    <BulkAssignRiskModal
                        show={showBulkAssignModal}
                        onClose={() => { setShowBulkAssignModal(false); setSelectedRemIds(new Set()); }}
                        risks={selected}
                        riskType={null}
                        refreshData={fetchRemediations}
                        dealId={sharedDealId}
                    />
                );
            })()}
        </div>
    );
};

export default Remediation;
